import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Group, Stack } from "@mantine/core";

import { format } from "date-fns";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { EncounterSummaryDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterSummaryDifference";
import DxDiagnosesVersionDiff from "@/encounters/components/DxPlan/DxDiagnosesVersionDiff";
import EncounterProceduresDiff from "@/encounters/components/EncounterProceduresDiff";
import ProceduresAmendmentDiff from "@/encounters/components/ProceduresAmendmentDiff";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { GynecologicalEncounterQuestionsLinkIds } from "@/encounters/constants/gynecological-encounter";
import { useEncounterDetailVersionPair } from "@/encounters/hooks/useEncounterDetailVersionPair";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import NotFound from "@/NotFound";

import GynecologicalDiffHeader from "./components/GynecologicalDiffHeader";
import GynecologicalEncounterDiff from "./components/GynecologicalEncounterDiff";
import GynecologicalPhysicalExamsDiff from "./components/GynecologicalPhysicalExamsDiff/GynecologicalPhysicalExamsDiff";
import VitalsSignDiff from "./components/VitalsSignDiff";

const GynecologicalVersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();
  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [amendmentHistory, setAmendmentHistory] = useState<QuestionnaireResponse[]>();
  //  TODO: add currentVersionIndex variable later to create a section in versionDiff
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const medplum = useMedplum();
  const navigate = useNavigate();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        if (!versionId || !encounterId) return;

        const encounter = await medplum.readResource("Encounter", encounterId);
        if (!encounter) return;

        setEncounter(encounter);

        const foundAmendmentHistory = await fetchAmendmentHistory(medplum, encounter);
        if (!foundAmendmentHistory) return;
        const currentHistoryVersionIndex = foundAmendmentHistory.findIndex(
          (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
        );

        if (currentHistoryVersionIndex === -1) return;

        setCurrentVersionIndex(currentHistoryVersionIndex);
        setCurrentVersion(foundAmendmentHistory[currentHistoryVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentHistoryVersionIndex + 1]);
        setAmendmentHistory(foundAmendmentHistory);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [versionId, encounterId, medplum]);

  const {
    currentEncounter,
    previousEncounter,
    isLoading: isEncounterPairLoading,
  } = useEncounterDetailVersionPair(encounterId, amendmentHistory, currentVersionIndex);

  if (isFetchingInitialData || !encounter || isEncounterPairLoading) return <Loading />;
  if ((!currentVersion || !previousVersion) && !isFetchingInitialData) return <NotFound />;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  return (
    <Stack py="md">
      <GynecologicalDiffHeader questionnaireResponse={currentVersion!} />
      <GynecologicalEncounterDiff
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        currentEncounter={currentEncounter}
        previousEncounter={previousEncounter}
      />
      <DxDiagnosesVersionDiff currentVersion={currentVersion} previousVersion={previousVersion} />
      <EncounterNoteDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        questionLinkId={GynecologicalEncounterQuestionsLinkIds.EncounterNote}
      />
      <EncounterSummaryDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
      />
      <VitalsSignDiff currentVersionIndex={currentVersionIndex} />
      <GynecologicalPhysicalExamsDiff
        versionId={versionId}
        currentVersionIndex={currentVersionIndex}
      />
      <EncounterProceduresDiff encounter={encounter!} />
      <ProceduresAmendmentDiff versionId={versionId} encounter={encounter!} />
      <FilesDiff currentVersionIndex={currentVersionIndex} />
      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button
            variant="outline"
            data-testid="exit-amendment-view-button"
            onClick={() => navigate(`/clients/${clientId}/encounters/${encounterId}`)}
          >
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default GynecologicalVersionDiff;
